import React, { createContext, useContext, useState, useCallback, useEffect, useMemo } from 'react';

interface ModalState {
  type: 'alert' | 'confirm';
  message: string;
  resolve: (value: boolean) => void;
}

interface ModalContextType {
  alert: (message: string) => Promise<void>;
  confirm: (message: string) => Promise<boolean>;
}

const ModalContext = createContext<ModalContextType | null>(null);

export const useModal = (): ModalContextType => {
  const context = useContext(ModalContext);
  if (!context) {
    throw new Error('useModal must be used within ModalProvider');
  }
  return context;
};

export const ModalProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [modal, setModal] = useState<ModalState | null>(null);

  const alert = useCallback((message: string): Promise<void> => {
    return new Promise((resolve) => {
      setModal({ type: 'alert', message, resolve: () => resolve() });
    });
  }, []);

  const confirm = useCallback((message: string): Promise<boolean> => {
    return new Promise((resolve) => {
      setModal({ type: 'confirm', message, resolve });
    });
  }, []);

  const handleConfirm = () => {
    modal?.resolve(true);
    setModal(null);
  };

  const handleCancel = () => {
    modal?.resolve(false);
    setModal(null);
  };

  useEffect(() => {
    if (!modal) return;
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        handleCancel();
      } else if (e.key === 'Enter') {
        handleConfirm();
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [modal]);

  const modalContextValue = useMemo(
    () => ({ alert, confirm }),
    [alert, confirm]
  );

  return (
    <ModalContext.Provider value={modalContextValue}>
      {children}
      {modal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[9999]">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-md mx-4 overflow-hidden">
            <div className="p-6">
              <p className="text-16-regular text-gray-900 whitespace-pre-line">{modal.message}</p>
            </div>
            <div className="flex border-t border-gray-200">
              {modal.type === 'confirm' && (
                <button
                  onClick={handleCancel}
                  className="flex-1 px-4 py-3 text-16-medium text-gray-700 hover:bg-gray-100 transition-colors border-r border-gray-200"
                >
                  취소
                </button>
              )}
              <button
                onClick={handleConfirm}
                className="flex-1 px-4 py-3 text-16-medium text-main hover:bg-gray-100 transition-colors"
                autoFocus
              >
                확인
              </button>
            </div>
          </div>
        </div>
      )}
    </ModalContext.Provider>
  );
};
